import { GridCallbackDetails, GridFilterModel, GridPaginationModel, GridSortModel, ValueOptions } from '@mui/x-data-grid-premium';
import React, { useEffect } from 'react';
import { useConfirmation } from 'src/base/ConfirmationDialog/ConfirmationService';
import { DataGrid, IGridColDef, Grid, Stack, IconButton, Button, Dialog } from 'src/components';
import DialogActions from 'src/components/DialogActions';
import DialogContent from 'src/components/DialogContent';
import DialogTitle from 'src/components/DialogTitle';
import ParametreGrupEnum from 'src/enums/ParametreGrupEnum';
import { translate } from 'src/i18n/tHelper';
import { ApiRequest } from 'src/models/api.request';
import VwHastauyari1DTO from 'src/models/HastaUyariModels/VwHastauyari1DTO';
import RecordInfo from 'src/parts/Genel/record.info';
import { ApiService } from 'src/services/core';
import { useHastaUyari1SilMutation } from 'src/services/HastaUyariListesi/HastaUyariListeApi';
import { useAppDispatch, useAppSelector } from 'src/store';
import { HastaUyariSlice } from 'src/store/Slices/HastaUyari/HastaUyariSlice';
import { useFindAuthByUrl } from 'src/utils/authUtils';

const translatePath = "pages|hastaUyari";
const HastaUyariListesi = (): JSX.Element => {
    const userInfo = useAppSelector(s => s.userLogin.userInfo);
    const state = useAppSelector(s => s.hastaUyari);
    const kimlikState = useAppSelector(s => s.kimlik);
    const muracaatState = useAppSelector(s => s.muracaat);
    const hastaServisState = useAppSelector(s => s.hastaServis);
    const parameters = useAppSelector(s => s.parameters.data);
    const dispatch = useAppDispatch();
    const confirm = useConfirmation();

    const authControl = useFindAuthByUrl();

    const [sil, silState] = useHastaUyari1SilMutation();

    const query = async (req: ApiRequest<VwHastauyari1DTO>) => {
        if (kimlikState.activeRow?.id > 0)
            req.filters.predicates.push({ fieldName: "kimlikId", operator: "=", value1: kimlikState.activeRow.id });

        var service = new ApiService<VwHastauyari1DTO>("HastaUyari");
        var res = await service.post("HastaUyari1Listesi", req);
        if (res.responseCode !== 200) {
            confirm({
                description: res.errorMessage,
                variant: "info",
            });
            dispatch(HastaUyariSlice.actions.setData({
                ...res,
                isFetching: false,
                isLoading: false,
            }));
        }
        dispatch(HastaUyariSlice.actions.setData({
            ...res,
            isFetching: false,
            isLoading: false,
        }));
    };

    useEffect(() => {
        if (!state.data.isLoading) return;
        dispatch(HastaUyariSlice.actions.setData({
            ...state.data,
            isFetching: true,
        }));
        let req: ApiRequest<VwHastauyari1DTO> = {
            paging: true,
            pageIndex: state.paginationModel.page,
            pageSize: state.paginationModel.pageSize,
            filters: { groupOperator: "and", predicates: [] },
            sorts: []
        };
        if (state.filterModel && state.filterModel.items?.length > 0) {
            state.filterModel.items.forEach(filterItem => {
                const operator = filterItem.operator === "contains" ? "like" :
                    filterItem.operator === "equals" ? "=" :
                        filterItem.operator === "startsWith" ? "startsWith" :
                            filterItem.operator === "endsWith" ? "endsWith" :
                                filterItem.operator === "isEmpty" ? "empty" :
                                    filterItem.operator === "isNotEmpty" ? "empty" :
                                        "like";

                req.filters.predicates.push({
                    fieldName: filterItem.field as keyof VwHastauyari1DTO,
                    operator: operator as "endsWith" | "startsWith" | "=" | ">" | ">=" | "<" | "<=" | "like" | "empty" | "in" | "between",
                    value1: filterItem.value,
                    value2: null,
                    not: filterItem.operator === "isNotEmpty" ? true : false,
                });
            });
        }
        if (state.sortModel && state.sortModel?.length > 0) {
            state.sortModel.forEach(sortItem => {
                req.sorts.push({
                    fieldName: sortItem.field as keyof VwHastauyari1DTO,
                    ascending: sortItem.sort === 'asc'
                });
            });
        } else {
            req.sorts.push({ fieldName: "id", ascending: false });
        }
        query(req);
    }, [state.paginationModel, state.sortModel, state.filterModel, state.data.isLoading]);

    const handleEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwHastauyari1DTO): void => {
        dispatch(HastaUyariSlice.actions.setActiveRow(row));
        dispatch(HastaUyariSlice.actions.setEkleOpen(true));
    }
    const handleDelete = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwHastauyari1DTO) => {
        if (!silState.isLoading) {
            const confirmResult = window.confirm(translate(translatePath, "Aktif Uyarı Kaydı Silinecektir. Onaylıyor musunuz ?"));
            if (confirmResult) {
                await sil(row).unwrap().then(() => {
                    dispatch(HastaUyariSlice.actions.setData({
                        ...state.data,
                        isLoading: true,
                    }));
                }).catch(e => e);
            }
        }
    }

    const columns: IGridColDef<VwHastauyari1DTO>[] = [
        {
            field: "muracaatId",
            headerName: translate(translatePath, "İşlem No"),
            width: 100,
            type: "number",
            noThousandSeparator: true,
        },
        {
            field: "adiSoyadi",
            headerName: translate(translatePath, "Hasta Adı Soyadı"),
            flex: 1,
            minWidth: 150,
        },
        {
            field: "uyaritarih",
            headerName: translate(translatePath, "Tarih Saat"),
            align: "center",
            type: "dateTime",
            minWidth: 155,
        },
        {
            field: "uyaritip",
            headerName: translate(translatePath, "Uyarı Tipi"),
            width: 150,
            type: "singleSelect",
            valueOptions: (params) => {
                return parameters.filter((f) => f.grupId === ParametreGrupEnum.UyariTipi).map((m) => {
                    return {
                        value: m.key,
                        label: m.value
                    } as ValueOptions;
                });
            }
        },
        {
            field: "uyariloginkod",
            headerName: translate(translatePath, "Uyarıyı Yazan"),
            flex: 1,
            width: 130,
        },
        {
            field: "personelAdiSoyadi",
            headerName: translate(translatePath, "Personel"),
            flex: 1,
            minWidth: 150,
        },
        {
            field: "aktif",
            headerName: translate(translatePath, "Aktif"),
            width: 65,
            type: "boolean",
            valueGetter: (params) => { return params.row?.aktif === "E"; },
        },
        {
            field: "moduladi",
            headerName: translate(translatePath, "Uyarının Hazırlandığı Modül"),
            width: 140,
        },
        {
            field: "action",
            headerName: translate(translatePath, "İşlemler"),
            width: 120,
            renderCell: (params) => {
                return (
                    <Stack direction="row">
                        <IconButton customType="edit" onClick={(e) => { handleEdit(e, params.row); }} />
                        <IconButton customType="delete" loading={params.row?.id === silState.originalArgs?.id && silState.isLoading} onClick={(e) => { handleDelete(e, params.row); }} />
                        <RecordInfo
                            id={params.row?.id}
                            eklemeTarihi={params.row.eklemeTarihi}
                            ekleyenKullanici={params.row.ekleyenKullanici}
                            guncellemeTarihi={params.row.guncellemeTarihi}
                            guncelleyenKullanici={params.row.guncelleyenKullanici}
                        />
                    </Stack>
                );
            },
        },
    ];
    const onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails): void => {
        dispatch(HastaUyariSlice.actions.setPaginationModel(model));
    }
    const onSortModelChange = (model: GridSortModel, details: GridCallbackDetails): void => {
        dispatch(HastaUyariSlice.actions.setSortModel(model));
    }
    const onFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<"filter">): void => {
        dispatch(HastaUyariSlice.actions.setFilterModel(model));
    }
    const handleSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (!state.data.isFetching)
            dispatch(HastaUyariSlice.actions.setData({
                ...state.data,
                isLoading: true,
            }));
        else
            dispatch(HastaUyariSlice.actions.setData({
                ...state.data,
                isLoading: false,
            }));
    }
    const handlerAdd = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        let kontrol: boolean = false;
        let row: VwHastauyari1DTO = new VwHastauyari1DTO();
        row.id = 0;
        row.uyaritip = 1;
        row.uyariModulId = authControl?.fkGnlModulId;
        row.uyaripersonelId = userInfo.personelId;
        if (kimlikState.activeRow?.id > 0) {
            kontrol = true;
        }
        if (kontrol) {
            row.fkBrans = muracaatState.activeRow?.fkHbsBransId ?? hastaServisState.activeRow?.fkHbsBransId ?? null;
            row.kimlikId = kimlikState.activeRow.id;
            row.adiSoyadi = `${kimlikState.activeRow?.adi} ${kimlikState.activeRow?.soyadi}`;
            row.muracaatId = muracaatState.activeRow?.id ?? null;
            dispatch(HastaUyariSlice.actions.setActiveRow(row));
            dispatch(HastaUyariSlice.actions.setEkleOpen(true));
        }
    }
    const handleClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        dispatch(HastaUyariSlice.actions.setOpen(false));
    }
    return (
        <Dialog fullWidth open={state.open} maxWidth="lg">
            <DialogTitle>{translate(translatePath, "Hasta Uyarı Ekranı")}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item>
                        <DataGrid
                            unstable_headerFilters
                            searchButton={<Button customType="search" onClick={handleSearch}>{state.data.isFetching ? translate(translatePath, "Aramayı Durdur") : translate(translatePath, "Ara")}</Button>}
                            addButton={<Button customType="add" onClick={handlerAdd} />}
                            pinnedColumns={{ right: ["action"] }}
                            sx={{ height: "60vh" }}
                            loading={state.data.isFetching}
                            rows={state.data?.data || []}
                            rowCount={state.data?.totalRecords || 0}
                            columns={columns}
                            pagination={true}
                            paginationMode="server"
                            sortingMode="server"
                            filterMode="server"
                            paginationModel={state.paginationModel}
                            onPaginationModelChange={onPaginationModelChange}
                            sortModel={state.sortModel}
                            onSortModelChange={onSortModelChange}
                            filterModel={state.filterModel}
                            onFilterModelChange={onFilterModelChange}
                            slots={{
                                headerFilterMenu: null
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent="flex-end">
                    <Grid item md={4} lg={3}>
                        <Button customType="close" onClick={handleClose} />
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default HastaUyariListesi;